import { http, HttpResponse, type HttpHandler } from "msw";

import { API_PREFIX, type PaginationMeta } from "@nasam/kernel";
import type { RoleDto } from "../data";
import { roleWriteDtoSchema } from "../data";
import { rolesFixtures } from "./rolesFixtures";

const TEMPLATES_PATH = `${API_PREFIX}/templates`;

export interface RolesHandlerOptions {
  readonly apiBaseUrl: string;
}

function pageMeta(
  url: URL,
  page: number,
  limit: number,
  totalCount: number,
): PaginationMeta {
  const totalPages = Math.ceil(totalCount / limit);
  const pageUrl = (target: number) => {
    const next = new URL(url);
    next.searchParams.set("page", String(target));
    next.searchParams.set("limit", String(limit));
    return next.toString();
  };
  return {
    totalCount,
    currentPage: page,
    totalPages,
    firstPageUrl: pageUrl(1),
    lastPageUrl: pageUrl(Math.max(1, totalPages)),
    nextPageUrl: page < totalPages ? pageUrl(page + 1) : null,
    previousPageUrl: page > 1 ? pageUrl(page - 1) : null,
  };
}
function number(value: string | null, fallback: number) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}
function matches(value: string, query: string | null): boolean {
  return (
    query === null ||
    value.toLocaleLowerCase().includes(query.toLocaleLowerCase())
  );
}
// `POST`/`PUT /templates` echo the template back without its `permissions[]`;
// only the read endpoints embed them.
function withoutPermissions(role: RoleDto): Omit<RoleDto, "permissions"> {
  return {
    id: role.id,
    accountId: role.accountId,
    nameEn: role.nameEn,
    nameAr: role.nameAr,
    isActive: role.isActive,
    createdAt: role.createdAt,
    updatedAt: role.updatedAt,
  };
}
function filter(roles: readonly RoleDto[], url: URL) {
  const params = url.searchParams;
  const search = params.get("search");
  const status = params.get("status");
  const sort = params.get("sort") === "desc" ? -1 : 1;
  return roles
    .filter(
      (role) =>
        search === null ||
        matches(role.nameEn, search) ||
        matches(role.nameAr, search),
    )
    .filter((role) =>
      status === null ? true : role.isActive === (status === "active"),
    )
    .toSorted(
      (left, right) => left.createdAt.localeCompare(right.createdAt) * sort,
    );
}

export function createRolesHandlers({
  apiBaseUrl,
}: RolesHandlerOptions): readonly HttpHandler[] {
  let roles = rolesFixtures.map((role) => ({ ...role }));
  return [
    http.get(`${apiBaseUrl}${TEMPLATES_PATH}`, ({ request }) => {
      const url = new URL(request.url);
      const page = number(url.searchParams.get("page"), 1);
      const limit = number(url.searchParams.get("limit"), 10);
      const items = filter(roles, url);
      return HttpResponse.json({
        data: items.slice((page - 1) * limit, page * limit),
        meta: pageMeta(url, page, limit, items.length),
      });
    }),
    http.get(`${apiBaseUrl}${TEMPLATES_PATH}/:id`, ({ params }) => {
      const role = roles.find((item) => item.id === params.id);
      return role === undefined
        ? HttpResponse.json(
            {
              statusCode: 404,
              path: TEMPLATES_PATH,
              message: "Role not found",
            },
            { status: 404 },
          )
        : HttpResponse.json({ data: role });
    }),
    http.post(`${apiBaseUrl}${TEMPLATES_PATH}`, async ({ request }) => {
      const input = roleWriteDtoSchema.parse(await request.json());
      const now = "2026-03-01T10:00:00.000Z";
      const role: RoleDto = {
        id: `role-${String(roles.length + 1).padStart(2, "0")}`,
        accountId: "account-01",
        nameEn: input.nameEn,
        nameAr: input.nameAr,
        isActive: input.isActive,
        permissions: [...input.permissions],
        createdAt: now,
        updatedAt: now,
      };
      roles = [...roles, role];
      return HttpResponse.json(
        { message: "Created successfully", data: withoutPermissions(role) },
        { status: 201 },
      );
    }),
    http.put(
      `${apiBaseUrl}${TEMPLATES_PATH}/:id`,
      async ({ params, request }) => {
        const input = roleWriteDtoSchema.parse(await request.json());
        const index = roles.findIndex((item) => item.id === params.id);
        if (index === -1) {
          return HttpResponse.json(
            {
              statusCode: 404,
              path: TEMPLATES_PATH,
              message: "Role not found",
            },
            { status: 404 },
          );
        }
        const current = roles[index] as RoleDto;
        const role: RoleDto = {
          ...current,
          nameEn: input.nameEn,
          nameAr: input.nameAr,
          isActive: input.isActive,
          permissions: [...input.permissions],
          updatedAt: "2026-03-01T10:00:00.000Z",
        };
        roles = roles.with(index, role);
        return HttpResponse.json({
          message: "Updated successfully",
          data: withoutPermissions(role),
        });
      },
    ),
    http.delete(`${apiBaseUrl}${TEMPLATES_PATH}/:id`, ({ params }) => {
      roles = roles.filter((item) => item.id !== params.id);
      return HttpResponse.json({ message: "Template deleted successfully." });
    }),
  ];
}
